'use client';

import { Alert } from 'antd';
import 'bootstrap-icons/font/bootstrap-icons.css';
import React from 'react';

import styles from './no-results.style';

/**
 * Props para el componente NoResults.
 */
interface NoResultsProps {
  /**
   * Una descripción personalizada opcional para mostrar.
   * Si no se proporciona, se mostrará un mensaje predeterminado.
   */
  description?: string;
  /**
   * Si es true, la descripción se renderizará como HTML.
   * Usar con precaución solo con contenido confiable.
   */
  allowHtml?: boolean;
  /**
   * Título personalizado opcional para mostrar.
   * Si no se proporciona, se mostrará "No se encontraron resultados".
   */
  title?: string;
  /**
   * Clase de icono personalizada opcional (Bootstrap Icons).
   * Si no se proporciona, se usará "bi bi-x-circle".
   */
  iconClass?: string;
  /**
   * Color personalizado opcional para el icono.
   * Si no se proporciona, se usará el color de error por defecto.
   */
  iconColor?: string;
}

/**
 * Un componente para mostrar un mensaje estandarizado de "No se encontraron resultados".
 * Es un contenedor estilizado alrededor del componente Alert de Ant Design.
 *
 * @param {NoResultsProps} props - Las props para el componente NoResults.
 *
 * @returns {JSX.Element} El componente NoResults renderizado.
 */
const NoResults = ({
  description,
  allowHtml = false,
  title,
  iconClass,
  iconColor
}: NoResultsProps) => {
  const defaultMessage =
    'Lo sentimos, pero no hemos podido encontrar ninguna coincidencia con tu búsqueda. Por favor, intenta con otros términos o ajusta los filtros.';
  const content = description || defaultMessage;
  const displayTitle = title || 'No se encontraron resultados';
  const displayIconClass = iconClass || 'bi bi-x-circle';

  return (
    <Alert
      type='info'
      showIcon
      banner
      icon={
        <i
          className={displayIconClass}
          style={{
            ...styles.noResults__icon,
            ...(iconColor && { color: iconColor })
          }}
        />
      }
      message={<h5 style={styles.noResults__title}>{displayTitle}</h5>}
      description={
        allowHtml ? (
          <p style={styles.noResults__text} dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <p style={styles.noResults__text}>{content}</p>
        )
      }
      style={styles.noResults}
    />
  );
};

export default NoResults;
